import { useCallback, useMemo, useState } from 'react';
import { useQuery } from '@common-fe/common-fe';
import _ from 'lodash';

import { api } from '@/api';
import { ALL_OPTION_KEY } from '@/common/constants';
import { API_URL, IReportFilterDtoDelinquentClaimsViewTypeEnum } from '@/dtos';
import { PLAN_YEAR_OPTIONS } from '@/modules/Reports/report.types';

import { useDownloadCSV, useGetEmployerIdParam } from '../../hooks';
import {
  DelinquentClaimsReportTableParams,
  DelinquentClaimsReportView,
} from '../DelinquentClaimsReport.types';

export const QUERY_KEY = 'EXPORT_DELINQUENT_CLAIMS_REPORT_CSV';

const useDelinquentClaimsReportExportCSVQuery = (params: DelinquentClaimsReportTableParams) => {
  const handleSaveFile = useDownloadCSV();
  const getEmployerIdParam = useGetEmployerIdParam({
    isMultipleEmployerMode: params.isMultipleEmployer,
  });
  const [isLoading, setIsLoading] = useState(false);

  const reportView = useMemo(
    () => params.reportView || DelinquentClaimsReportView.ByEmployee,
    [params.reportView]
  );

  const { refetch } = useQuery(
    [QUERY_KEY],
    () =>
      api.get(API_URL.REPORTS_DELINQUENT_CLAIMS_REPORT_BREAKDOWN_EXPORT_CSV, {
        params: _.pickBy({
          ...getEmployerIdParam(params.employerId, params.employerIds),
          show_ssn: params.isSSNShowed,
          isSSNShowed: params.isSSNShowed,
          search_value: params.searchString,
          delinquent_claims_view_type:
            reportView === DelinquentClaimsReportView.ByClaim
              ? IReportFilterDtoDelinquentClaimsViewTypeEnum.BY_CLAIM
              : IReportFilterDtoDelinquentClaimsViewTypeEnum.BY_EMPLOYEE,
          account_types:
            params.accountTypes && !params.accountTypes.includes(ALL_OPTION_KEY)
              ? params.accountTypes.join(',')
              : undefined,
          plan_ids:
            params.isMultipleEmployer && params.employerIds?.length !== 1
              ? undefined
              : params.planIds && !params.planIds.includes(ALL_OPTION_KEY)
                ? params.planIds.join(',')
                : undefined,
          plan_year_periods:
            params.isMultipleEmployer && params.employerIds?.length !== 1
              ? PLAN_YEAR_OPTIONS[2].key
              : params.planYearPeriod && !params.planYearPeriod.includes(ALL_OPTION_KEY)
                ? params.planYearPeriod.join(',')
                : undefined,
          claim_statuses:
            params.reportView !== DelinquentClaimsReportView.ByClaim
              ? undefined
              : params.claimStatuses && !params.claimStatuses.includes(ALL_OPTION_KEY)
                ? params.claimStatuses.join(',')
                : undefined,
        }),
      }),
    {
      retry: 0,
      enabled: false,
    }
  );

  const handleExport = useCallback(async () => {
    try {
      setIsLoading(true);
      const response = await refetch();
      handleSaveFile(response.data?.data, 'csv', 'delinquent_claims');
    } finally {
      setIsLoading(false);
    }
  }, [handleSaveFile, refetch]);

  return {
    isLoading,
    handleExport,
  };
};

export default useDelinquentClaimsReportExportCSVQuery;
